import React from 'react';
import PropTypes from 'prop-types';
import {DateTimeHelpers, Utils} from 'poly-js-utils';
import _ from 'underscore';
import { connectToJSContext, RA_73_STERILIZE_HTML_STRING } from '../utils.jsx';
import {DefaultEvent} from './default_event.jsx';
import SectionHeader from '../section_header/index.jsx';
import {checkEmptyState} from '../helpers';
import {EVENT_LIST} from '../constants';
import {formatEventLink} from '../events_common';
import EventDetails from '../event_details';

const EventList = (props) => {
    const {
        showHeader,
        visibleEvents,
        visibleEventsLength,
        rawEventsLength,
        eventsEmptyTextState,
        labelDisclosure,
        labelRsvp,
        labelRsvpButtonText,
        labelDetailsButton,
        pageTitle,
        urlSlug,
        viewMoreButtonText,
        viewMoreButtonLabel,
        switchRenderingOrder,
        isEventDetailsEnabled,
        shouldDisplayOtherEventsCTA,
        otherEventsLinkUrl,
        slug,
        isExternalUrlWithReference,
        sectionId,
        setSelectedEventId,
        selectedEventId
    } = props;
    return (
        <div className="c-event hs-section">
            { showHeader && (rawEventsLength || eventsEmptyTextState) &&
                <SectionHeader
                    title={pageTitle}
                    urlSlug={urlSlug}
                    buttonText={viewMoreButtonText}
                    buttonLabel={viewMoreButtonLabel}
                    switchRenderingOrder={switchRenderingOrder}
                    buttonAttributes={{
                        "data-event-name": "",
                        "data-click-type": "events-view-all"
                    }}
                    sectionId={sectionId}
                />
            }
            <div className="events">
                <div className="hs-events container">
                    <div className="row">
                        {!visibleEventsLength &&
                            <div className="col-xs-12">
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: RA_73_STERILIZE_HTML_STRING(eventsEmptyTextState)
                                    }}
                                />
                            </div>
                        }
                        {visibleEvents.map((event, eventIndex) => {
                            const { eventId } = event;
                            if (typeof selectedEventId === 'undefined') {
                                return (
                                    <DefaultEvent
                                        {...event}
                                        labelRsvp={labelRsvp}
                                        labelRsvpButton={labelRsvpButtonText}
                                        labelDetailsButton={labelDetailsButton}
                                        shouldDisplayEventDetailsLink={isEventDetailsEnabled}
                                        slug={slug}
                                        handleDetailsLinkClick={() => setSelectedEventId(eventId)}
                                        isExternalUrlWithReference={isExternalUrlWithReference}
                                        key={eventIndex}
                                    />
                                );
                            }
                            // double equal, due to string number type discrepancy
                            // eslint-disable-next-line eqeqeq
                            if (selectedEventId == eventId) {
                                return (
                                    <EventDetails
                                        title={event.title}
                                        startDateTime={event.startDateTime}
                                        endDateTime={event.endDateTime}
                                        locationVenueName={event.locationVenueName}
                                        locationAddress={event.locationAddress}
                                        description={event.description}
                                        otherEventsLinkUrl={otherEventsLinkUrl}
                                        image={event.image}
                                        rsvpType={event.rsvpType}
                                        rsvpInfo={event.rsvpInfo}
                                        rsvpLink={event.rsvpLink}
                                        shouldDisplayOtherEventsCTA={shouldDisplayOtherEventsCTA}
                                        disclosure={labelDisclosure}
                                        labelRsvp={labelRsvp}
                                        labelRsvpButtonText={labelRsvpButtonText}
                                        key={eventIndex}
                                    />
                                );
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

EventList.propTypes = {
    visibleEvents: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            locationVenueName: PropTypes.string,
            locationAddress: PropTypes.string,
            description: PropTypes.string,
            rsvpType: PropTypes.string,
            rsvpInfo: PropTypes.string,
            rsvpLink: PropTypes.string,
            startDateTime: PropTypes.string.isRequired,
            endDateTime: PropTypes.string,
            image: PropTypes.string,
            eventId: PropTypes.number
        })
    ).isRequired,
    visibleEventsLength: PropTypes.number.isRequired,
    rawEventsLength: PropTypes.number.isRequired,
    eventsEmptyTextState: PropTypes.string.isRequired,
    labelDisclosure: PropTypes.string,
    labelRsvp: PropTypes.string.isRequired,
    labelRsvpButtonText: PropTypes.string.isRequired,
    labelDetailsButton: PropTypes.string.isRequired,
    pageTitle: PropTypes.string,
    urlSlug: PropTypes.string,
    viewMoreButtonText: PropTypes.string,
    viewMoreButtonLabel: PropTypes.string,
    switchRenderingOrder: PropTypes.bool,
    isEventDetailsEnabled: PropTypes.bool,
    shouldDisplayOtherEventsCTA: PropTypes.bool,
    showHeader: PropTypes.bool, // TODO where does it come from?
    slug: PropTypes.string,
    isExternalUrlWithReference: PropTypes.bool,
    setSelectedEventId: PropTypes.func
};

EventList.defaultProps = {
    labelRsvp: EVENT_LIST.DEFAULT_PROPS.LABEL_RSVP,
    labelRsvpButtonText: EVENT_LIST.DEFAULT_PROPS.LABEL_RSVP_BUTTON,
    labelDetailsButton: EVENT_LIST.DEFAULT_PROPS.LABEL_DETAILS_BUTTON,
    showHeader: EVENT_LIST.DEFAULT_PROPS.SHOW_HEADER,
    isEventDetailsEnabled: EVENT_LIST.DEFAULT_PROPS.IS_EVENT_DETAILS_ENABLED
};

export { EventList };

const mapStateToProps = (state, originalProps) => {
    const {
        slug,
        events: rawEvents = [],
        base_link: baseLink,
        theme_data: {
            events_page: {
                is_event_details_enabled: isEventDetailsEnabledSelectBox,
                should_display_other_events_cta: shouldDisplayOtherEventsCTASelectBox,
                empty_state_text: eventEmptyTextStateData,
                header_title: pageTitle,
                default_image: defaultEventImage,
                view_more_text: viewMoreButtonText,
                view_more_label: viewMoreButtonLabel,
                switch_rendering_order: switchRenderingOrderData
            } = {},
            labels: {
                event_disclosure: labelDisclosure,
                event_details_button_text: labelDetailsButton,
                event_rsvp_button_text: labelRsvpButtonText,
                event_rsvp: labelRsvp
            } = {},
            nav: {
                main_navigation_links: mainNavigationLinks,
                is_external_url_with_reference: isExternalUrlWithReferenceSelectBox
            }
        }
    } = state;
    const {
        limit: eventsLimit
    } = originalProps;

    // eslint-disable-next-line underscore/matches-shorthand, underscore/identity-shorthand
    const { url: urlSlug } = _.find(mainNavigationLinks, ({ url }) => url === 'events') || {};
    const shouldDisplayOtherEventsCTA = Utils.selectBoxIsTrue(shouldDisplayOtherEventsCTASelectBox);
    const isEventDetailsEnabled = Utils.selectBoxIsTrue(isEventDetailsEnabledSelectBox);
    const isExternalUrlWithReference = Utils.selectBoxIsTrue(isExternalUrlWithReferenceSelectBox);
    const eventsEmptyTextState = checkEmptyState(eventEmptyTextStateData);
    const rawEventsLength = rawEvents.length;
    const switchRenderingOrder = Utils.selectBoxIsTrue(switchRenderingOrderData);

    const visibleEventsRaw = eventsLimit
        ? rawEvents.slice(0, eventsLimit)
        : rawEvents;
    const visibleEvents = visibleEventsRaw.map(({
        event_name: title,
        event_date,
        event_date_end,
        details: description,
        photo: eventImage,
        location_text: locationAddress,
        location_displayed: locationVenueName,
        rsvp_type: rsvpType,
        rsvp_info: rsvpInfo,
        id: eventId
    }) => ({
        eventId,
        title,
        locationVenueName,
        locationAddress,
        description,
        rsvpType,
        rsvpInfo,
        rsvpLink: formatEventLink({ rsvpInfo, rsvpType, eventId, baseLink }),
        startDateTime: DateTimeHelpers.formatDate(event_date),
        endDateTime: DateTimeHelpers.formatDate(event_date_end),
        image: eventImage || defaultEventImage || null
    }));
    const visibleEventsLength = visibleEvents.length;
    const otherEventsLinkUrl = `${baseLink}events`;
    return {
        visibleEvents,
        visibleEventsLength,
        rawEventsLength,
        eventsEmptyTextState,
        labelDisclosure: labelDisclosure || undefined, // we need undefined for default props
        labelRsvp: labelRsvp || undefined,
        labelRsvpButtonText: labelRsvpButtonText || undefined,
        labelDetailsButton: labelDetailsButton || undefined,
        pageTitle,
        urlSlug,
        viewMoreButtonText,
        viewMoreButtonLabel,
        switchRenderingOrder,
        shouldDisplayOtherEventsCTA,
        otherEventsLinkUrl,
        isEventDetailsEnabled,
        slug,
        isExternalUrlWithReference,
        sectionId: 'events'
    };
};

export default connectToJSContext(mapStateToProps)(EventList);



// WEBPACK FOOTER //
// ./components/event_list/index.jsx